/**
 * Core route group.
 *
 * Root/dashboard, customers, vehicle loans, bank-transfer payments, open
 * banking, accounts, institutions, users, profile, deposits, API management
 * and gateway routes.
 *
 * Extracted verbatim from AppRoutes.tsx. The declaration sequence is preserved
 * exactly as part of the route structural contract; see
 * `__tests__/AppRoutes.structure.test.tsx`.
 */

import { lazy } from 'react';
import { Route, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '../../auth/components/ProtectedRoute';
import { ROUTES, permissionsFor, requiresAnyPermission } from '../routeCatalogue';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

const Dashboard = lazy(() => import('../../components/Dashboard/Dashboard'));
const NewInstitution = lazy(() => import('../../components/SuperAdmin/NewInstitution.js'));
const Institutions = lazy(() => import('../../components/SuperAdmin/Institutions'));
const InstitutionDetails = lazy(() => import('../../components/SuperAdmin/InstitutionDetails'));
const Users = lazy(() => import('../../components/Users/Users'));
const UserDetail = lazy(() => import('../../components/Users/UserDetail'));
const Profile = lazy(() => import('../../components/Profile/Profile'));
const Customers = lazy(() => import('../../components/Customers').then(m => ({ default: m.Customers })));
const RetailUserDetail = lazy(() => import('../../components/Customers/RetailUserDetail'));
const VehicleLoans = lazy(() => import('../../components/VehicleLoans/VehicleLoans').then(m => ({ default: m.VehicleLoans })));
const DepositProductsPage = lazy(() => import('../../features/deposits/pages/DepositProducts'));
const DepositAccountsPage = lazy(() => import('../../features/deposits/pages/Accounts'));
const InterestRatesPage = lazy(() => import('../../features/deposits/pages/InterestRates'));
const MaturitiesPage = lazy(() => import('../../features/deposits/pages/Maturities'));
const DepositApplicationsPage = lazy(() => import('../../features/deposits/pages/Applications'));
const DepositNoticesPage = lazy(() => import('../../features/deposits/pages/Notices'));
const DepositReportsPage = lazy(() => import('../../features/deposits/pages/Reports'));
const DepositSettingsPage = lazy(() => import('../../features/deposits/pages/Settings'));
const DepositTransfersPage = lazy(() => import('../../features/deposits/pages/Transfers'));
const PayByBank = lazy(() => import('../../components/Payments/PayByBank'));
const OpenBankingRoutes = lazy(() => import('../../features/openbanking/routes').then(m => ({ default: m.OpenBankingRoutes })));
const ApiManagement = lazy(() => import('../../features/api-management/pages/ApiManagement'));
const GatewayRouteList = lazy(() => import('../../features/gateway-routes/pages/RouteList'));
const GatewayRouteDetail = lazy(() => import('../../features/gateway-routes/pages/RouteDetail'));
const GatewayCreateRoute = lazy(() => import('../../features/gateway-routes/pages/CreateRoute'));
const InstitutionBuilder = lazy(() => import('../../features/institution-builder/pages/InstitutionBuilder'));
const AccountList = lazy(() => import('../../pages/accounts/AccountList'));
const AccountDetail = lazy(() => import('../../pages/accounts/AccountDetail'));
const OpenAccount = lazy(() => import('../../pages/accounts/OpenAccount'));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DepositProductsLegacyRedirect = () => {
    const location = useLocation();
    return (
        <Navigate
            to={{
                pathname: ROUTES.DEPOSITS_PRODUCTS.path,
                search: location.search,
                hash: location.hash,
            }}
            replace
        />
    );
};

// ---------------------------------------------------------------------------
// coreRoutes()
// ---------------------------------------------------------------------------

export function coreRoutes() {
    return (
        <>
        <Route path={ROUTES.ROOT.path} element={<Navigate to="/dashboard" replace />} />
        <Route path={ROUTES.DASHBOARD.path} element={<Dashboard />} />

        {/* Customer Routes - requires customers:view */}
        <Route path={ROUTES.CUSTOMERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.CUSTOMERS)}>
                <Customers />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.CUSTOMERS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.CUSTOMERS_DETAIL)}>
                <RetailUserDetail />
            </ProtectedRoute>
        } />

        {/* Vehicle Loans - uses loan-related permissions */}
        <Route path={ROUTES.VEHICLE_LOANS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.VEHICLE_LOANS)}>
                <VehicleLoans />
            </ProtectedRoute>
        } />

        {/* Payment Routes - requires transactions:create */}
        <Route path={ROUTES.PAYMENTS_BANK_TRANSFER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PAYMENTS_BANK_TRANSFER)}>
                <PayByBank />
            </ProtectedRoute>
        } />

        {/* Open Banking Routes - requires accounts:view */}
        <Route path={ROUTES.OPENBANKING_SECTION.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.OPENBANKING_SECTION)}>
                <OpenBankingRoutes />
            </ProtectedRoute>
        } />

        {/* Account Management Routes - requires accounts:view */}
        <Route path={ROUTES.ACCOUNTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ACCOUNTS)}>
                <AccountList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ACCOUNTS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ACCOUNTS_DETAIL)}>
                <AccountDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ACCOUNTS_NEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ACCOUNTS_NEW)}>
                <OpenAccount />
            </ProtectedRoute>
        } />

        {/* Institutions Routes - requires platform:institutions:manage */}
        <Route path={ROUTES.INSTITUTIONS_NEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.INSTITUTIONS_NEW)}>
                <NewInstitution />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.INSTITUTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.INSTITUTIONS)}>
                <Institutions />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.INSTITUTIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.INSTITUTIONS_DETAIL)}>
                <InstitutionDetails />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.INSTITUTIONS_DETAIL_APP_CONFIG.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.INSTITUTIONS_DETAIL_APP_CONFIG)}>
                <InstitutionBuilder />
            </ProtectedRoute>
        } />

        {/* Users Routes - requires users:view */}
        <Route path={ROUTES.USERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.USERS)}>
                <Users />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.USERS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.USERS_DETAIL)}>
                <UserDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.USERS_DETAIL_EDIT.path} element={
            <ProtectedRoute
                requiredPermissions={permissionsFor(ROUTES.USERS_DETAIL_EDIT)}
                requireAny={requiresAnyPermission(ROUTES.USERS_DETAIL_EDIT)}
            >
                <UserDetail />
            </ProtectedRoute>
        } />

        {/* Profile Route - always accessible to authenticated users */}
        <Route path={ROUTES.PROFILE.path} element={<Profile />} />

        {/* Deposit Products — canonical under /deposits/*; legacy path redirects */}
        <Route path={ROUTES.DEPOSITS_PRODUCTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_PRODUCTS)}>
                <DepositProductsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSIT_PRODUCTS_LEGACY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSIT_PRODUCTS_LEGACY)}>
                <DepositProductsLegacyRedirect />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_ACCOUNTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_ACCOUNTS)}>
                <DepositAccountsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_RATES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_RATES)}>
                <InterestRatesPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_MATURITIES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_MATURITIES)}>
                <MaturitiesPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_APPLICATIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_APPLICATIONS)}>
                <DepositApplicationsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_NOTICES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_NOTICES)}>
                <DepositNoticesPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_REPORTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_REPORTS)}>
                <DepositReportsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_SETTINGS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_SETTINGS)}>
                <DepositSettingsPage />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.DEPOSITS_TRANSFERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.DEPOSITS_TRANSFERS)}>
                <DepositTransfersPage />
            </ProtectedRoute>
        } />

        {/* API Management Route- requires settings:security */}
        <Route path={ROUTES.API_MANAGEMENT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.API_MANAGEMENT)}>
                <ApiManagement />
            </ProtectedRoute>
        } />

        {/* Gateway Routes - requires platform:routes:manage */}
        <Route path={ROUTES.GATEWAY_ROUTES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GATEWAY_ROUTES)}>
                <GatewayRouteList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GATEWAY_ROUTES_NEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GATEWAY_ROUTES_NEW)}>
                <GatewayCreateRoute />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.GATEWAY_ROUTES_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.GATEWAY_ROUTES_DETAIL)}>
                <GatewayRouteDetail />
            </ProtectedRoute>
        } />
        </>
    );
}
